import type { AnyRouter } from "@tanstack/react-router";
import {
  endScreenRenderSpan,
  startScreenRenderSpan,
} from "~/lib/sentry/screenRenderSpan";
import {
  registerScreenViewForPath,
  takePendingScreenView,
  trackFirebaseScreenView,
} from "./firebase";
import { normalizeScreenPathname } from "./screenPathname";
import { isScreenReadyRoute } from "./screenReadyRoutes";
import {
  beginScreenRender,
  markScreenFirstPaint,
  markScreenHidden,
  setScreenRenderSink,
} from "./screenRender";

/** Firebase の画面 ID が無い画面の screen.id にする、ルートパターン（/_authenticated と末尾スラッシュを除く） */
export const toRouteScreenId = (routeId: string): string => {
  const stripped = routeId.replace(/^\/_authenticated/, "");
  const trimmed =
    stripped.length > 1 && stripped.endsWith("/")
      ? stripped.slice(0, -1)
      : stripped;
  return trimmed || "/";
};

/**
 * 画面描画時間の計測をルーターに配線する（main.tsx で 1 回）。
 * - T0: onBeforeLoad（redirect 連鎖の各ホップでも無条件に出る。onBeforeNavigate は使わない）
 * - 現在画面の更新と screen_view の送信待ち登録: onResolved
 * - T1: onRendered 直後の rAF、hidden: visibilitychange
 * - T2: Template の useScreenReady（screenRender.markScreenReady）
 * 送り先は Sentry（ui.render スパン + measurement）と Firebase（screen_view。送信だけ描画完了まで遅らせる）。
 */
export const setupScreenRenderTracking = (router: AnyRouter): void => {
  // onResolved 時点の pathname → routeId（確定時に screen.id のフォールバックに使う）
  const routeIdByPathname = new Map<string, string>();

  setScreenRenderSink((result) => {
    // onResolved で登録した { screenId, source }。追跡対象外の画面なら null
    const view = takePendingScreenView(result.pathname);
    const routeId = routeIdByPathname.get(result.pathname);
    routeIdByPathname.delete(result.pathname);
    const readyKind =
      result.status === "ready" || result.status === "first_paint"
        ? result.status
        : null;
    endScreenRenderSpan({
      pathname: result.pathname,
      status: result.status,
      renderMs: result.renderMs,
      firstPaintMs: result.firstPaintMs,
      screenId: view?.screenId ?? (routeId ? toRouteScreenId(routeId) : null),
      readyKind,
      isInitial: result.isInitial,
    });
    // screen_view はここで初めて送る（render_ms が無いケースも件数維持のため送る）。render_ms の付与は辞書承認後
    if (view) trackFirebaseScreenView(view);
  });

  router.subscribe("onBeforeLoad", ({ fromLocation, toLocation }) => {
    const pathname = normalizeScreenPathname(toLocation.pathname);
    // search params だけの変化は画面遷移ではない
    if (
      fromLocation &&
      pathname === normalizeScreenPathname(fromLocation.pathname)
    ) {
      return;
    }
    // 起動の最初の load。redirect 連鎖の 2 本目以降も fromLocation は無く、beginScreenRender が T0 を引き継ぐ
    const begin = beginScreenRender(pathname, {
      isInitial: !fromLocation,
      instrumented: isScreenReadyRoute(pathname),
    });
    startScreenRenderSpan(pathname, begin.isInitial, begin.startedAt);
  });
  router.subscribe("onResolved", ({ toLocation }) => {
    registerScreenViewForPath(toLocation.pathname);
    const last = router.state.matches.at(-1);
    if (last) {
      if (routeIdByPathname.size > 100) routeIdByPathname.clear();
      routeIdByPathname.set(
        normalizeScreenPathname(toLocation.pathname),
        last.routeId,
      );
    }
  });
  router.subscribe("onRendered", () => {
    markScreenFirstPaint();
  });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") markScreenHidden();
  });
};
